import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { notificationTemplateService } from '@/services/notificationTemplateService'
import {
  NotificationTemplate,
  CreateNotificationTemplateRequest,
  TemplatePreviewRequest,
  TemplatePreviewResponse,
  TemplateMetadata
} from '@shared/types/notificationTemplate'

interface NotificationTemplateState {
  templates: NotificationTemplate[]
  selectedTemplate: NotificationTemplate | null
  templateMetadata: Record<string, TemplateMetadata> | null
  preview: TemplatePreviewResponse | null
  loading: boolean
  error: string | null
}

const initialState: NotificationTemplateState = {
  templates: [],
  selectedTemplate: null,
  templateMetadata: null,
  preview: null,
  loading: false,
  error: null
}

// Async thunks
export const fetchAllTemplates = createAsyncThunk(
  'notificationTemplates/fetchAll',
  async (type: string = 'EMAIL') => {
    const templates = await notificationTemplateService.getAllTemplates(type)
    return templates
  }
)

export const fetchTemplateVersions = createAsyncThunk(
  'notificationTemplates/fetchVersions',
  async ({ name, type = 'EMAIL' }: { name: string; type?: string }) => {
    const templates = await notificationTemplateService.getTemplateVersions(name, type)
    return templates
  }
)

export const fetchTemplateById = createAsyncThunk(
  'notificationTemplates/fetchById',
  async (id: string) => {
    const template = await notificationTemplateService.getTemplateById(id)
    return template
  }
)

export const createTemplate = createAsyncThunk(
  'notificationTemplates/create',
  async (data: CreateNotificationTemplateRequest) => {
    const template = await notificationTemplateService.createTemplate(data)
    return template
  }
)

export const activateTemplate = createAsyncThunk(
  'notificationTemplates/activate',
  async (id: string) => {
    const template = await notificationTemplateService.activateTemplate(id)
    return template
  }
)

export const previewTemplate = createAsyncThunk(
  'notificationTemplates/preview',
  async (data: TemplatePreviewRequest) => {
    const preview = await notificationTemplateService.previewTemplate(data)
    return preview
  }
)

export const deleteTemplate = createAsyncThunk(
  'notificationTemplates/delete',
  async (id: string) => {
    await notificationTemplateService.deleteTemplate(id)
    return id
  }
)

export const fetchTemplateMetadata = createAsyncThunk(
  'notificationTemplates/fetchMetadata',
  async () => {
    const metadata = await notificationTemplateService.getTemplateMetadata()
    return metadata
  }
)

const notificationTemplateSlice = createSlice({
  name: 'notificationTemplates',
  initialState,
  reducers: {
    setSelectedTemplate: (state, action: PayloadAction<NotificationTemplate | null>) => {
      state.selectedTemplate = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    clearPreview: (state) => {
      state.preview = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all templates
      .addCase(fetchAllTemplates.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllTemplates.fulfilled, (state, action) => {
        state.loading = false
        state.templates = (action.payload as any)?.data || action.payload || []
      })
      .addCase(fetchAllTemplates.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch templates'
      })

      // Fetch template versions
      .addCase(fetchTemplateVersions.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTemplateVersions.fulfilled, (state, action) => {
        state.loading = false
        state.templates = (action.payload as any)?.data || action.payload || []
      })
      .addCase(fetchTemplateVersions.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch template versions'
      })

      // Fetch template by ID
      .addCase(fetchTemplateById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTemplateById.fulfilled, (state, action) => {
        state.loading = false
        state.selectedTemplate = (action.payload as any)?.data || action.payload || null
      })
      .addCase(fetchTemplateById.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch template'
      })

      // Create template
      .addCase(createTemplate.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.loading = false
        const template = (action.payload as any)?.data || action.payload
        if (template) {
          state.templates.push(template)
        }
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create template'
      })

      // Activate template
      .addCase(activateTemplate.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(activateTemplate.fulfilled, (state, action) => {
        state.loading = false
        // Extract template data from wrapped response
        const template = (action.payload as any)?.data || action.payload
        if (template) {
          // Update the template in the list
          const index = state.templates.findIndex(t => t.id === template.id)
          if (index !== -1) {
            // Deactivate all versions of this template first
            state.templates = state.templates.map(t =>
              t.name === template.name && t.type === template.type
                ? { ...t, isActive: t.id === template.id }
                : t
            )
          }
        }
      })
      .addCase(activateTemplate.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to activate template'
      })

      // Preview template
      .addCase(previewTemplate.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(previewTemplate.fulfilled, (state, action) => {
        state.loading = false
        state.preview = (action.payload as any)?.data || action.payload || null
      })
      .addCase(previewTemplate.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to preview template'
      })

      // Delete template
      .addCase(deleteTemplate.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.loading = false
        state.templates = state.templates.filter(t => t.id !== action.payload)
        if (state.selectedTemplate?.id === action.payload) {
          state.selectedTemplate = null
        }
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete template'
      })

      // Fetch template metadata
      .addCase(fetchTemplateMetadata.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTemplateMetadata.fulfilled, (state, action) => {
        state.loading = false
        state.templateMetadata = (action.payload as any)?.data || action.payload || null
      })
      .addCase(fetchTemplateMetadata.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch template metadata'
      })
  }
})

export const { setSelectedTemplate, clearError, clearPreview } = notificationTemplateSlice.actions
export default notificationTemplateSlice.reducer
